import {
    IonButton,
    IonFooter,
    IonIcon,
    IonTextarea,
    useIonActionSheet,
    useIonAlert,
    useIonRouter,
    useIonViewDidEnter,
    useIonViewWillLeave,
} from '@ionic/react'
import clsx from 'clsx'
import { ellipsisVertical, send as sendIcon } from 'ionicons/icons'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import Button from '../../components/Button'
import ContactPartner from '../../components/Contact/ContactPartner'
import ContactReportModal from '../../components/Contact/ContactReportModal'
import { dayLabel, isSameDay, messageTime } from '../../components/Contact/format'
import Content from '../../components/Content'
import Header from '../../components/Header'
import Page from '../../components/Page'
import {
    ContactMessageStatus,
    ContactRequestStatus,
    ContactTargetType,
    useAcceptContactRequestMutation,
    useBlockContactUserMutation,
    useContactConversationHeaderQuery,
    useContactConversationQuery,
    useDeclineContactRequestMutation,
    useMarkContactConversationReadMutation,
    useSendContactMessageMutation,
    useWithdrawContactRequestMutation,
} from '../../lib/apollo/types'
import { useUser } from '../../providers/Auth/hooks'
import useToast from '../../providers/Toast/hooks'

import './style.scss'

/** Nachfrage-Takt, solange der Chat sichtbar ist (Konzept: 3 bis 5 Sekunden). */
const OPEN_CHAT_POLL_INTERVAL = 5000

const MAX_MESSAGE_LENGTH = 500

const ContactConversation: React.FC = () => {
    const { conversationId } = useParams<{ conversationId: string }>()
    const { t } = useTranslation()
    const [present] = useToast()
    const [confirm] = useIonAlert()
    const [showActions] = useIonActionSheet()
    const router = useIonRouter()
    const user = useUser()
    const [body, setBody] = useState('')
    const [reporting, setReporting] = useState(false)
    const contentRef = useRef<HTMLIonContentElement | null>(null)

    const { data, startPolling, stopPolling } = useContactConversationQuery({
        variables: { id: conversationId },
        skip: !conversationId,
    })

    // Getrennt von der Polling-Abfrage: Name und Bild der Gegenseite kosten in
    // der Middleware einen Upstream-Aufruf und aendern sich nicht im
    // Fuenf-Sekunden-Takt.
    const { data: headerData, refetch: refetchHeader } =
        useContactConversationHeaderQuery({
            variables: { id: conversationId },
            skip: !conversationId,
        })

    // Das Abzeichen an der Tab-Leiste haengt an `contactUnread` und laeuft
    // sonst im 30-Sekunden-Takt hinterher — es stuende also noch eine Weile
    // eine Zahl fuer Nachrichten da, die man gerade liest.
    const [markRead] = useMarkContactConversationReadMutation({
        refetchQueries: ['contactUnread'],
    })

    const [sendMessage, { loading: sending }] = useSendContactMessageMutation({
        onError: () => {
            present(
                t(
                    'contact.message.failed',
                    'Die Nachricht konnte nicht gesendet werden.',
                ),
                'danger',
            )
        },
        refetchQueries: ['contactConversation'],
    })

    const answerRefetch = {
        refetchQueries: [
            'contactConversation',
            'contactConversationHeader',
            'contactRequests',
            'contactRequestsWithPeople',
        ],
    }
    const [accept] = useAcceptContactRequestMutation(answerRefetch)
    const [decline] = useDeclineContactRequestMutation(answerRefetch)
    // Beim Zurueckziehen verschwindet die Unterhaltung ganz. Deshalb hier
    // bewusst nicht `answerRefetch`: `contactConversation` wuerde gegen eine
    // geloeschte Unterhaltung laufen und die Mutation ueber ihren Refetch
    // scheitern lassen. Nachgeladen wird nur, was es danach noch gibt.
    const [withdraw] = useWithdrawContactRequestMutation({
        refetchQueries: [
            'contactRequests',
            'contactRequestsWithPeople',
            'contactConversations',
            'contactUnread',
        ],
    })

    const [block] = useBlockContactUserMutation({
        refetchQueries: [
            'contactConversations',
            'contactUnread',
            'contactRequests',
            'contactRequestsWithPeople',
            'contactBlockedUsers',
        ],
        onError: () => {
            present(
                t(
                    'contact.block.failed',
                    'Der Kontakt konnte nicht blockiert werden.',
                ),
                'danger',
            )
        },
    })

    // Schneller Takt nur, solange die Seite wirklich sichtbar ist — sonst
    // fragt die App im Hintergrund dauerhaft alle fuenf Sekunden nach. Ionic
    // haelt verlassene Seiten im Stapel montiert, deshalb ist "sichtbar" ein
    // eigener Zustand und nicht einfach "gerendert".
    const [visible, setVisible] = useState(false)

    useIonViewDidEnter(() => {
        setVisible(true)
        startPolling(OPEN_CHAT_POLL_INTERVAL)
    })

    useIonViewWillLeave(() => {
        setVisible(false)
        stopPolling()
    })

    const conversation = data?.contactConversation
    const header = headerData?.contactConversation
    const request = header?.request
    const isAccepted = conversation?.status === ContactRequestStatus.Accepted
    const isPending = conversation?.status === ContactRequestStatus.Pending
    const me = user?.user.id
    const iAmTheSender = !!request && request.fromUserId === me

    const messages = useMemo(
        () => conversation?.messages ?? [],
        [conversation?.messages],
    )

    // Die Gegenseite wird dem Absender erst mit der Annahme genannt. Die
    // Annahme faellt hier aber ueber das Polling ein, nicht ueber diese
    // Abfrage — also nachfragen, sobald der Status umspringt.
    useEffect(() => {
        if (isAccepted && !header?.counterpart) {
            refetchHeader()
        }
    }, [isAccepted, header?.counterpart, refetchHeader])

    /**
     * Gelesen ist, was jemand vor sich hat — nicht nur, was beim Oeffnen schon
     * dastand. Der Chat fragt alle fuenf Sekunden nach; haengte das Markieren
     * allein am Betreten der Seite, zaehlte das Abzeichen die Nachrichten hoch,
     * waehrend man sie liest, und sie stuenden beim naechsten Besuch wieder als
     * ungelesen da.
     *
     * Die Antwort der Mutation traegt dieselben Nachrichten mit `read` zurueck
     * in den Cache. `hasUnread` wird damit falsch, und der Effekt laeuft nicht
     * erneut — nur deshalb dreht sich das hier nicht im Kreis.
     */
    const hasUnread = useMemo(
        () =>
            messages.some(
                (message) =>
                    message.fromUserId !== me &&
                    message.status === ContactMessageStatus.Sent,
            ),
        [messages, me],
    )

    useEffect(() => {
        if (!visible || !hasUnread || !conversationId) return

        markRead({ variables: { id: conversationId } })
    }, [visible, hasUnread, conversationId, markRead])

    useEffect(() => {
        if (messages.length === 0) return
        contentRef.current?.scrollToBottom(300)
    }, [messages.length])

    const send = useCallback(async () => {
        const text = body.trim()
        if (!text) return

        const { data: sent } = await sendMessage({
            variables: { input: { conversationId, body: text } },
        })

        if (sent) {
            setBody('')
        }
    }, [body, conversationId, sendMessage])

    /**
     * Nach dem Zurueckziehen oder Blockieren gibt es diese Unterhaltung fuer
     * uns nicht mehr — die Seite wuerde sonst im naechsten Polling-Takt gegen
     * eine leere Antwort laufen.
     */
    const leave = useCallback(() => {
        stopPolling()
        router.push('/inbox/chats', 'back', 'pop')
    }, [router, stopPolling])

    const onWithdraw = useCallback(() => {
        if (!request) return

        // Zurueckziehen loescht auch beim Empfaenger — das ist eine andere
        // Zusage als frueher, und deshalb wird hier gefragt.
        confirm({
            header: t('contact.request.withdraw', 'Zurückziehen'),
            message: t(
                'contact.request.withdrawConfirm',
                'Die Anfrage und deine Nachricht werden auch beim Empfänger gelöscht. Das lässt sich nicht rückgängig machen.',
            ),
            buttons: [
                { text: t('buttons.cancel'), role: 'cancel' },
                {
                    text: t('contact.request.withdraw', 'Zurückziehen'),
                    handler: async () => {
                        await withdraw({ variables: { id: request.id } })
                        present(
                            t(
                                'contact.request.withdrawn',
                                'Anfrage zurückgezogen.',
                            ),
                            'success',
                        )
                        leave()
                    },
                },
            ],
        })
    }, [request, withdraw, present, t, confirm, leave])

    const onBlock = useCallback(() => {
        confirm({
            header: t('contact.block.title', 'Kontakt blockieren'),
            message: t(
                'contact.block.confirm',
                'Du bekommst von diesem Kontakt keine Nachrichten und Anfragen mehr, und die Unterhaltung verschwindet aus deinem Posteingang. Die Blockierung kannst du in den Einstellungen wieder aufheben.',
            ),
            buttons: [
                { text: t('buttons.cancel'), role: 'cancel' },
                {
                    text: t('contact.block.submit', 'Blockieren'),
                    handler: async () => {
                        const { data: blocked } = await block({
                            variables: { conversationId },
                        })
                        if (!blocked) return

                        present(
                            t('contact.block.done', 'Kontakt blockiert.'),
                            'success',
                        )
                        leave()
                    },
                },
            ],
        })
    }, [confirm, t, block, conversationId, present, leave])

    const onAccept = useCallback(async () => {
        if (!request) return
        await accept({ variables: { id: request.id } })
        present(t('contact.request.accepted', 'Anfrage angenommen.'), 'success')
    }, [request, accept, present, t])

    const onDecline = useCallback(async () => {
        if (!request) return
        await decline({ variables: { id: request.id } })
        present(t('contact.request.declined', 'Anfrage abgelehnt.'), 'success')
    }, [request, decline, present, t])

    const openActions = useCallback(() => {
        showActions({
            buttons: [
                {
                    text: t('contact.report.title', 'Profil melden'),
                    role: 'destructive',
                    handler: () => setReporting(true),
                },
                {
                    text: t('contact.block.title', 'Kontakt blockieren'),
                    handler: onBlock,
                },
                { text: t('buttons.cancel'), role: 'cancel' },
            ],
        })
    }, [showActions, t, onBlock])

    /**
     * Vor der Annahme kennt der Absender die Gegenseite nicht — dann steht dort
     * das, was er tatsaechlich angeschrieben hat: die Karte oder das Profil.
     */
    const partnerName = header?.counterpart
        ? `${header.counterpart.firstName} ${header.counterpart.lastName}`
        : request?.target?.name

    const partnerImage = header?.counterpart?.image ?? request?.target?.image

    const partnerSubline =
        request?.target?.type === ContactTargetType.Talent && request.target.name
            ? t('contact.request.viaTalent', {
                defaultValue: 'über die Talent Card {{name}}',
                name: request.target.name,
            })
            : undefined

    return (
        <Page trackingTitle='Contact Conversation'>
            <Header
                title={t('contact.conversation.title', 'Kontakt')}
                defaultHref='/inbox/chats'
            >
                {/*
                  * Nur, wenn die Gegenseite ueberhaupt genannt ist: Vor der
                  * Annahme kennt der Absender sie nicht, und dann waeren
                  * Melden und Blockieren ein Griff ins Dunkle.
                  */}
                {header?.counterpart && (
                    <IonButton
                        onClick={openActions}
                        aria-label={t('contact.conversation.more', 'Mehr')}
                    >
                        <IonIcon
                            slot='icon-only'
                            icon={ellipsisVertical}
                        />
                    </IonButton>
                )}
            </Header>
            <Content ref={contentRef}>
                <div className='contact-conversation'>
                    <div className='contact-conversation__partner'>
                        <ContactPartner
                            name={partnerName}
                            image={partnerImage}
                            subline={partnerSubline}
                        />
                    </div>

                    <div className='contact-conversation__thread'>
                        {isPending && (
                            <div className='contact-conversation__notice'>
                                <div className='contact-conversation__notice-title'>
                                    {iAmTheSender
                                        ? t(
                                            'contact.conversation.pending.sent',
                                            'Kontaktanfrage gesendet',
                                        )
                                        : t(
                                            'contact.conversation.pending.received',
                                            'Kontaktanfrage erhalten',
                                        )}
                                </div>
                                <p className='contact-conversation__notice-text'>
                                    {iAmTheSender
                                        ? t(
                                            'contact.conversation.pending',
                                            'Warte auf Annahme. Sobald die Anfrage angenommen wurde, könnt ihr schreiben.',
                                        )
                                        : t(
                                            'contact.conversation.pending.answer',
                                            'Nimm die Anfrage an, um zu antworten.',
                                        )}
                                </p>
                                {request && (
                                    <div className='contact-conversation__notice-actions'>
                                        {iAmTheSender ? (
                                            <Button
                                                size='small'
                                                fill='clear'
                                                onClick={onWithdraw}
                                            >
                                                {t(
                                                    'contact.request.withdraw',
                                                    'Zurückziehen',
                                                )}
                                            </Button>
                                        ) : (
                                            <>
                                                <Button
                                                    size='small'
                                                    fill='solid'
                                                    color='secondary'
                                                    onClick={onAccept}
                                                >
                                                    {t(
                                                        'contact.request.accept',
                                                        'Annehmen',
                                                    )}
                                                </Button>
                                                <Button
                                                    size='small'
                                                    fill='clear'
                                                    onClick={onDecline}
                                                >
                                                    {t(
                                                        'contact.request.decline',
                                                        'Ablehnen',
                                                    )}
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {messages.map((message, index) => {
                            const own = message.fromUserId === me
                            const previous = messages[index - 1]
                            const newDay =
                                !previous ||
                                !isSameDay(previous.createdAt, message.createdAt)

                            return (
                                <Fragment key={message.id}>
                                    {newDay && (
                                        <div className='contact-conversation__day'>
                                            {dayLabel(message.createdAt, t)}
                                        </div>
                                    )}
                                    <div
                                        className={clsx(
                                            'contact-conversation__message',
                                            {
                                                'contact-conversation__message--own':
                                                    own,
                                            },
                                        )}
                                    >
                                        <p className='n2br contact-conversation__body'>
                                            {message.body}
                                        </p>
                                        <div className='contact-conversation__meta'>
                                            {messageTime(message.createdAt)}
                                            {own && (
                                                <>
                                                    {' · '}
                                                    {message.status ===
                                                    ContactMessageStatus.Read
                                                        ? t(
                                                            'contact.message.read',
                                                            'gelesen',
                                                        )
                                                        : t(
                                                            'contact.message.sent',
                                                            'gesendet',
                                                        )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </Fragment>
                            )
                        })}

                        {conversation && !isPending && !isAccepted && (
                            <p className='contact-conversation__hint'>
                                {t(
                                    'contact.conversation.closed',
                                    'Diese Unterhaltung ist beendet.',
                                )}
                            </p>
                        )}
                    </div>
                </div>
            </Content>

            {isAccepted && (
                <IonFooter className='contact-conversation__footer'>
                    <div className='contact-conversation__composer'>
                        <IonTextarea
                            className='contact-conversation__input'
                            autoGrow
                            rows={1}
                            maxlength={MAX_MESSAGE_LENGTH}
                            value={body}
                            placeholder={t(
                                'contact.message.placeholder',
                                'Nachricht schreiben',
                            )}
                            // Ionic 7 meldet `ionChange` beim Textarea erst
                            // beim Verlassen des Feldes — damit bliebe der
                            // Senden-Knopf waehrend des Tippens deaktiviert.
                            onIonInput={(event) => {
                                setBody(event.detail.value ?? '')
                            }}
                        />
                        <Button
                            className='contact-conversation__send'
                            fill='solid'
                            color='secondary'
                            icon={sendIcon}
                            round
                            loading={sending}
                            disabled={!body.trim() || sending}
                            onClick={send}
                            aria-label={t('contact.message.send', 'Senden')}
                        />
                    </div>
                </IonFooter>
            )}

            <ContactReportModal
                isOpen={reporting}
                onClose={() => setReporting(false)}
                conversationId={conversationId}
                partnerName={partnerName}
                onBlocked={leave}
            />
        </Page>
    )
}

export default ContactConversation
